import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, ExternalLink, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Logo } from "../components/site/Logo";
import { LeadsTab } from "../components/admin/LeadsTab";
import { CasesTab } from "../components/admin/CasesTab";
import { ContentEditor } from "../components/admin/ContentEditor";
import { MetaCapiPanel } from "../components/admin/MetaCapiPanel";
import { api } from "../lib/api";
import { DEFAULTS, deepMerge } from "../lib/content";

const TABS = [
  { v: "leads", l: "Richieste" },
  { v: "cases", l: "Casi studio" },
  { v: "home", l: "Testi e immagini home" },
  { v: "program", l: "Pagina programma" },
  { v: "company", l: "Azienda e contatti" },
  { v: "settings", l: "SEO, tracking, email" },
];

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border hairline bg-white/[0.02] p-4">
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p data-testid={testId} className="mt-1 font-display font-extrabold text-3xl text-white">{value ?? "–"}</p>
  </div>
);

export default function Admin() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState({});

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadStats();
        return api.get("/admin/content");
      })
      .then((r) => setContent(deepMerge(DEFAULTS, r?.data)))
      .catch(() => nav("/admin/login"));
  }, [nav, loadStats]);

  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };

  if (!user || !content) return <div className="min-h-screen grid place-items-center bg-[#060a14]"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#060a14] text-white">
      <header className="sticky top-0 z-30 border-b hairline bg-[#060a14]/90 backdrop-blur">
        <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo /><span className="hidden sm:inline font-mono text-[11px] text-slate-400 uppercase tracking-[0.2em]">Admin</span></div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" data-testid="admin-view-site" className="h-9 px-3 rounded-full border hairline text-sm inline-flex items-center gap-2 hover:bg-white/5"><ExternalLink className="w-4 h-4" /><span className="hidden sm:inline">Vedi sito</span></Link>
            <button data-testid="admin-logout" onClick={logout} className="h-9 px-3 rounded-full border hairline text-sm inline-flex items-center gap-2 hover:bg-white/5"><LogOut className="w-4 h-4" /><span className="hidden sm:inline">Esci</span></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="Richieste totali" value={stats.leads_total} testId="stat-leads-total" />
          <Stat label="Da gestire" value={stats.leads_new} testId="stat-leads-new" />
          <Stat label="Casi studio" value={stats.cases_total} testId="stat-cases-total" />
          <Stat label="Pubblicati" value={stats.cases_published} testId="stat-cases-published" />
        </div>
        <Tabs defaultValue="leads">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-white/[0.03] border hairline p-1 rounded-xl">
            {TABS.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`} className="rounded-lg data-[state=active]:bg-[#ff5500] data-[state=active]:text-white text-slate-300">{t.l}</TabsTrigger>)}
          </TabsList>
          <div className="mt-6 rounded-3xl border hairline bg-[#0a0f1d] p-5 sm:p-7">
            <TabsContent value="leads"><LeadsTab onChange={loadStats} /></TabsContent>
            <TabsContent value="cases"><CasesTab /></TabsContent>
            <TabsContent value="home"><ContentEditor section="home" initial={content.home} hint="Testi e immagini della homepage. Nei titoli, la frase dopo il primo punto viene evidenziata con il colore." /></TabsContent>
            <TabsContent value="program"><ContentEditor section="program" initial={content.program} /></TabsContent>
            <TabsContent value="company"><ContentEditor section="company" initial={content.company} hint="Dati aziendali mostrati nel footer e nella privacy policy, numero e messaggio del pulsante WhatsApp." /></TabsContent>
            <TabsContent value="settings" className="space-y-10">
              <div><h3 className="font-display-n font-bold text-lg mb-4">Email notifiche richieste</h3><ContentEditor section="notifications" initial={content.notifications} hint="Indirizzo che riceve una email per ogni nuova richiesta di consulenza." /></div>
              <div><h3 className="font-display-n font-bold text-lg mb-4">SEO</h3><ContentEditor section="seo" initial={content.seo} /></div>
              <div><h3 className="font-display-n font-bold text-lg mb-4">Tracking e conversioni</h3><ContentEditor section="tracking" initial={content.tracking} hint="Inserisci gli ID di GA4, GTM e Meta Pixel: gli script si attivano solo dopo il consenso cookie. Per ogni evento scegli dal menu l'evento Meta (Visualizzazione contenuto, Lead, Acquisto...), quando deve scattare e, per le pagine, incolla l'URL. Ricordati di premere Salva." /></div>
              <div><h3 className="font-display-n font-bold text-lg mb-4">Meta Conversions API</h3><MetaCapiPanel /></div>
            </TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
